import React, { useState, useEffect } from "react";
import { ECLUnsafeStringifyULID } from "../../../../utilities/ECLUUID";
import useSelectorId from "../../../../utilities/hooks/useSelectorId";
import {
   ECLDateTimePicker,
   ECLLoader,
   ECLNumber,
   ECLSelect,
} from "../../../index";
import * as arrow from "apache-arrow";
import "./ECLParameters.css";
import debounce from "../../../../utilities/debounce";
import {
   endpointRequest,
   updateServerSession,
} from "../../../../utilities/requests";

export default function ECLParameters(props) {
   const [constraints, setConstraints] = useState(null);
   const [currentMinValue, setCurrentMinValue] = useState(null);
   const [currentMaxValue, setCurrentMaxValue] = useState(null);
   const [currentMinDatetime, setCurrentMinDatetime] = useState(null);
   const [currentMaxDatetime, setCurrentMaxDatetime] = useState(null);
   const [selectState, setSelectState] = useState(null);
   const [inputType, setInputType] = useState(null);
   const [precision, setPrecision] = useState(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);
   const instanceId = useSelectorId();

   const getValues = () => {
      const min = document.querySelector(
         "#param-range-min" + instanceId,
      )?.value;
      const max = document.querySelector(
         "#param-range-max" + instanceId,
      )?.value;
      return [new Date(min), new Date(max)];
   };

   const handleMin = (e) => {
      const newMin = Number(e.target.value);
      const newMax =
         currentMaxValue !== null && newMin > currentMaxValue
            ? newMin
            : currentMaxValue;

      // Update the UI manually
      const minInput = document.querySelector("#param-filter-min" + instanceId);
      const maxInput = document.querySelector("#param-filter-max" + instanceId);

      if (minInput) minInput.value = newMin;
      if (maxInput && newMin > currentMaxValue) maxInput.value = newMin;

      // Update backend
      minMaxBackendChanges(newMin, newMax);
   };

   const handleMax = (e) => {
      const newMax = Number(e.target.value);
      const newMin =
         currentMinValue !== null && newMax < currentMinValue
            ? newMax
            : currentMinValue;

      // Update the UI manually
      const maxInput = document.querySelector("#param-filter-max" + instanceId);
      const minInput = document.querySelector("#param-filter-min" + instanceId);

      if (maxInput) maxInput.value = newMax;
      if (minInput && newMax < currentMinValue) minInput.value = newMax;

      // Update backend
      minMaxBackendChanges(newMin, newMax);
   };

   const minMaxBackendChanges = debounce((newMin, newMax) => {
      const key_vals = [
         {
            key: props.block.data.property,
            value: {
               min: newMin,
               max: newMax,
            },
         },
      ];
      updateServerSession(props, key_vals);
   }, 400);

   const selectStateBackendUpdate = debounce((newSelectState) => {
      const selectedValues = newSelectState
         .filter((option) => option.selected)
         .map((option) => option.searchableString);
      const key_vals = [
         {
            key: props.block.data.property,
            value: {
               selections: selectedValues,
            },
         },
      ];
      updateServerSession(props, key_vals);
   }, 400);

   const handleSelectStateChange = (newSelectState) => {
      setSelectState(newSelectState);
      // Debounced backend update
      selectStateBackendUpdate(newSelectState);
   };

   const datetimeBackendChanges = debounce((currentMinMaxValues) => {
      const key_vals = [
         {
            key: props.block.data.property,
            value: {
               datetime_min: currentMinMaxValues[0],
               datetime_max: currentMinMaxValues[1],
            },
         },
      ];
      updateServerSession(props, key_vals);
   }, 400);

   const handleDatetimeChange = (currentMinMaxValues) => {
      setCurrentMinDatetime(currentMinMaxValues[0]);
      setCurrentMaxDatetime(currentMinMaxValues[1]);
      datetimeBackendChanges(currentMinMaxValues);
   };

   useEffect(() => {
      const rootID = props.headlist.at(-1); //Last item on the headlist is your root
      const sessionID = props.flatDoc[rootID].session;
      endpointRequest(props.parentObject, props.block.id, sessionID, "AllData")
         .then((res) => {
            switch (
               res.columns[0].typeId //Switch on Arrow type
            ) {
               case arrow.Type.Binary:
               case arrow.Type.FixedSizeBinary:
                  setInputType("text");
                  setSelectState(
                     res.rows.map((r) => {
                        return {
                           id: ECLUnsafeStringifyULID(r[0]),
                           searchableString: ECLUnsafeStringifyULID(r[0]),
                           jsx: ECLUnsafeStringifyULID(r[0]),
                           selected: selectState
                              ? selectState
                                   .filter((s) => s.selected)
                                   .find(
                                      (s) =>
                                         s.searchableString ===
                                         ECLUnsafeStringifyULID(r[0]),
                                   )
                              : r[1],
                        };
                     }),
                  );
                  setLoading(false);
                  break;
               case arrow.Type.Bool:
               case arrow.Type.Utf8:
                  setInputType("text");
                  setSelectState(
                     res.rows.map((r, i) => {
                        return {
                           id: `param-option-${instanceId}-${i}`,
                           searchableString: r[0].toString(),
                           jsx: r[0].toString().trim(),
                           selected: selectState
                              ? selectState
                                   .filter((s) => s.selected)
                                   .find(
                                      (s) =>
                                         s.searchableString === r[0].toString(),
                                   )
                              : r[1],
                        };
                     }),
                  );
                  setLoading(false);
                  break;
               case arrow.Type.Int:
               case arrow.Type.Int8:
               case arrow.Type.Int16:
               case arrow.Type.Int32:
               case arrow.Type.Int64:
                  setInputType("number");
                  setConstraints([
                     res.rows?.at(0)?.at(0),
                     res.rows?.at(0)?.at(1),
                  ]);
                  setCurrentMinValue(res.rows?.at(0)?.at(2));
                  setCurrentMaxValue(res.rows?.at(0)?.at(3));
                  setPrecision(0);
                  setLoading(false);
                  break;
               case arrow.Type.Decimal:
               case arrow.Type.Float:
               case arrow.Type.Float16:
               case arrow.Type.Float32:
               case arrow.Type.Float64:
                  setInputType("number");
                  setConstraints([
                     res.rows?.at(0)?.at(0),
                     res.rows?.at(0)?.at(1),
                  ]);
                  setCurrentMinValue(res.rows?.at(0)?.at(2));
                  setCurrentMaxValue(res.rows?.at(0)?.at(3));
                  setPrecision(2);
                  setLoading(false);
                  break;
               case arrow.Type.Date:
               case arrow.Type.DateMillisecond:
               case arrow.Type.Timestamp:
                  setInputType("datetime");
                  setConstraints([
                     res.rows?.at(0)?.at(0),
                     res.rows?.at(0)?.at(1),
                  ]);
                  setCurrentMinDatetime(res.rows?.at(0)?.at(2));
                  setCurrentMaxDatetime(res.rows?.at(0)?.at(3));
                  setLoading(false);
                  break;
               default:
                  break;
            }
         })
         .catch((error) => {
            console.error(error);
            setError("Error retrieving filters");
         });
   }, [props.block.react.refresh]);

   if (loading || error) {
      return <ECLLoader message={error} />;
   }

   return (
      <form
         className="parameter-block-container"
         id={"parameter-block-container-" + instanceId}
      >
         <label htmlFor={"parameter-block-container-" + instanceId}>
            {props.block.data.name}
         </label>
         {{
            text: selectState && (
               <div className="parameter-select-container">
                  <ECLSelect
                     options={selectState}
                     setOptions={handleSelectStateChange}
                     multiselect={props.block.data.multiselect}
                  />
               </div>
            ),
            number: constraints && (
               <>
                  <div className="parameter-field-container">
                     <ECLNumber
                        id={"param-filter-min" + instanceId}
                        name="Min"
                        min={constraints[0]}
                        max={constraints[1]}
                        precision={precision}
                        value={currentMinValue}
                        onChange={handleMin}
                     />
                  </div>
                  <div className="parameter-field-container">
                     <ECLNumber
                        id={"param-filter-max" + instanceId}
                        name="Max"
                        min={constraints[0]}
                        max={constraints[1]}
                        precision={precision}
                        value={currentMaxValue}
                        onChange={handleMax}
                     />
                  </div>
               </>
            ),
            datetime: constraints && (
               <>
                  <div className="parameter-field-container">
                     <ECLDateTimePicker
                        id={"param-range-min" + instanceId}
                        name={"Min"}
                        date={currentMinDatetime}
                        onChange={() => {
                           handleDatetimeChange(getValues());
                        }}
                     />
                  </div>
                  <div className="parameter-field-container">
                     <ECLDateTimePicker
                        id={"param-range-max" + instanceId}
                        name={"Max"}
                        date={currentMaxDatetime}
                        onChange={() => {
                           handleDatetimeChange(getValues());
                        }}
                     />
                  </div>
               </>
            ),
         }[inputType] || <ECLLoader message={error || "Unknown Filter Type"} />}
      </form>
   );
}
